import {randomIndexOf, randomOf} from './Utility'

interface QuizUserInterface {
  progress: number,
  score: number, 
  tippGiven: boolean
}

interface ApiGetCurrentQuizInterface {
  api: string,
  title: string,
  subtitle: string,
  changed_on: string, 
  static: string, // or boolean?
  quiz_data: ApiQuizDataInterface
}

interface ApiQuizDataInterface {
  intros: Array<AudioTextInterface>,
  questions: Array<ApiQuestionInterface>,
  reports: Array<{
    audio: string,
    text: string, 
    image: ApiImageInterface
  }>,
  transition: {
    fallback: {
      intro: AudiosTextsInterface,
      new_game: AudiosTextsInterface
    },
    wrong: AudiosTextsInterface,
    right: AudiosTextsInterface,
    next: AudiosTextsInterface,
    last: AudiosTextsInterface,
    new_game: AudiosTextsInterface,
    confirm_new_game: AudiosTextsInterface,
    repeat: AudiosTextsInterface,
    stop: AudiosTextsInterface,
    finish: AudiosTextsInterface
  },
  background_image: ApiImageInterface
}

interface ApiQuestionInterface {
  audio: {
    question: string,
    tipp: string,
    solved: string,
    not_solved: string
  },
  text: {
    question: string,
    tipp: string,
    solved: string,
    not_solved: string
  },
  solution: {
    options: Array<string>,
    correct: Array<number>, // string or number?
    slotvalues: Array<{
      value: string,
      synonyms: Array<string>
    }>
  },
  image: ApiImageInterface
}

interface AudioTextInterface {
  audio: string,
  text: string
}

interface AudiosTextsInterface {
  audios: Array<string>,
  texts: Array<string>
}

interface ApiImageInterface {
  xl: string,
  l: string, 
  m: string,
  s: string,
  square: string
}

const _randomTransitionAudioAndText = (transition: {audios: Array<string>, texts: Array<string>}) => {
  let i = randomIndexOf(transition.audios)
  return {
    audio: transition.audios[i],
    text: transition.texts[i]
  }
}


class Quiz {
  data: ApiQuizDataInterface
  user: QuizUserInterface
  current: ApiQuestionInterface

  constructor(quizData: ApiQuizDataInterface, user: QuizUserInterface = {progress: 0, tippGiven: false, score: 0}){
    this.data = quizData
    this.user = user
    this.current = this.data.questions[this.user.progress]

    return this
  }

  getRandomIntro(){ return randomOf(this.data.intros) }
  getQuizImage(){ return this.data.background_image.l }

  // Fallbacks: 

  getRandomIntroFallback(){ return _randomTransitionAudioAndText(this.data.transition.fallback.intro) }
  getRandomNewGameFallback(){ return _randomTransitionAudioAndText(this.data.transition.fallback.new_game) }

  // transition:

  getRandomTransitionWrong(){ return _randomTransitionAudioAndText(this.data.transition.wrong) }
  getRandomTransitionRight(){ return _randomTransitionAudioAndText(this.data.transition.right) }
  getRandomTransitionNext(){ return _randomTransitionAudioAndText(this.data.transition.next) }
  getRandomTransitionLast(){ return _randomTransitionAudioAndText(this.data.transition.last) }
  getRandomTransitionNewGame(){ return _randomTransitionAudioAndText(this.data.transition.new_game) }
  getRandomTransitionConfirmNewGame(){ return _randomTransitionAudioAndText(this.data.transition.confirm_new_game) }
  getRandomTransitionRepeat(){ return _randomTransitionAudioAndText(this.data.transition.repeat) }
  getRandomTransitionStop(){ return _randomTransitionAudioAndText(this.data.transition.stop) }
  getRandomTransitionFinish(){ return _randomTransitionAudioAndText(this.data.transition.finish) }

  // Question:

  getCurrentQuestionAudio(){ return this.current.audio.question }
  getCurrentQuestionText(){ return this.current.text.question }

  getCurrentTippAudio(){ return this.current.audio.tipp }
  getCurrentTippText(){ return this.current.text.tipp }

  getCurrentSolvedAudio(){ return this.current.audio.solved }
  getCurrentSolvedText(){ return this.current.text.solved }

  getCurrentNotSolvedAudio(){ return this.current.audio.not_solved }
  getCurrentNotSolvedText(){ return this.current.text.not_solved }

  getCurrentQuestionImage(){ return this.current.image.square }

  getCurrentAnswerEntitiesGoogle(){
    return {
      name: 'quizAnswer',
      entities : this.current.solution.slotvalues,
      // entityOverrideMode: 'ENTITY_OVERRIDE_MODE_SUPPLEMENT' // temporarily commented out because TypeScript stinks
    }
  }
  getCurrentAnswerEntitiesAlexa(){
    return [{
      name: 'quizAnswer',
      values: this.current.solution.slotvalues.map(answerObj => {
        return {name: {value: answerObj.value, synonyms: answerObj.synonyms}}
      })
    }]
  }

  getCurrentOptions(){ return this.current.solution.options }

  getCorrectAnswers(){ 
    return this.current.solution.options.filter((answer, i) => { 
      return this.current.solution.correct.includes(i) 
    })
  }
  getFirstCorrectAnswer(){ return this.getCorrectAnswers()[0] }


  // Reports aka Quiz Results: 

  getReportBestAudio() { return this.data.reports[3].audio }
  getReportBestText() { return this.data.reports[3].text }
  getReportBestImage() { return this.data.reports[3].image.l }

  getReportGoodAudio() { return this.data.reports[2].audio }
  getReportGoodText() { return this.data.reports[2].text }
  getReportGoodImage() { return this.data.reports[2].image.l }

  getReportBadAudio() { return this.data.reports[1].audio }
  getReportBadText() { return this.data.reports[1].text }
  getReportBadImage() { return this.data.reports[1].image.l }

  getReportWorstAudio() { return this.data.reports[0].audio }
  getReportWorstText() { return this.data.reports[0].text }
  getReportWorstImage() { return this.data.reports[0].image.l }


  // Logical Methods:
  setIncreasedScore(){ // only if answer is correct
    // Award only half the points if user needed tipp
    this.user.score = this.user.score + (this.user.tippGiven ? 0.5 : 1)

    return this.user.score
  }
  setNextQuestion(){
    this.user.progress++
    this.user.tippGiven = false
    this.current = this.data.questions[this.user.progress]

    return this.current
  }
  isCurrentQuestionlast(){
    return this.user.progress == (this.data.questions.length - 1)
  }
  getScoreInPercent(){
    return this.user.score / this.data.questions.length * 100
  }
  isTippGiven(){ return this.user.tippGiven }
  setTippGiven(){ return this.user.tippGiven = true }

  getNumberOfQuestions(){ return this.data.questions.length }
}

export {
  QuizUserInterface,
  ApiGetCurrentQuizInterface,
  ApiQuizDataInterface,
  ApiQuestionInterface,
  AudioTextInterface,
  AudiosTextsInterface,
  ApiImageInterface,
  Quiz
}